import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { API, colors } from "../shared/constants";
import { useIsMobile } from "../shared/utils";

function FitBounds({ sites }) {
  const map = useMap();
  useEffect(() => {
    if (sites.length === 0) return;
    const bounds = sites.map(s => [parseFloat(s.latitude), parseFloat(s.longitude)]);
    if (bounds.length === 1) {
      map.setView(bounds[0], 12);
    } else {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
    }
  }, [sites, map]);
  return null;
}

const pinIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 40" width="28" height="40">
    <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26S28 24.5 28 14C28 6.27 21.73 0 14 0z"
      fill="#0891b2" stroke="#1e3a5f" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5" fill="white"/>
  </svg>`,
  iconSize: [28, 40],
  iconAnchor: [14, 40],
  popupAnchor: [0, -42],
});

// Amber teardrop pin with a person silhouette — matches site pin shape language
function makeTechIcon(name) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return L.divIcon({
    className: "",
    html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 46" width="32" height="46">
      <title>${initials}</title>
      <path d="M16 0C7.16 0 0 7.16 0 16c0 12 16 30 16 30S32 28 32 16C32 7.16 24.84 0 16 0z"
        fill="#f59e0b" stroke="#1e3a5f" stroke-width="1.5"/>
      <circle cx="16" cy="11" r="4.5" fill="white"/>
      <path d="M7 27c0-5 4-9 9-9s9 4 9 9" fill="white"/>
    </svg>`,
    iconSize: [32, 46],
    iconAnchor: [16, 46],
    popupAnchor: [0, -48],
  });
}

const NOMINATIM_DELAY_MS = 1100;

function KpiCard({ label, value, sub, accentColor, onClick, placeholder }) {
  const isMobile = useIsMobile();
  const [hovered, setHovered] = useState(false);
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => clickable && setHovered(true)}
      onMouseLeave={() => clickable && setHovered(false)}
      style={{
        backgroundColor: hovered ? "#f8fafc" : colors.white,
        border: `1px solid ${colors.border}`,
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: "8px",
        padding: isMobile ? "10px 12px" : "14px 16px",
        cursor: clickable ? "pointer" : "default",
        transition: "background-color 0.15s",
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: "10px", fontWeight: "700", color: colors.slate, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "6px" }}>
        {label}
      </div>
      <div style={{ fontSize: isMobile ? "20px" : "28px", fontWeight: "800", color: placeholder ? "#cbd5e1" : colors.darkGrey, lineHeight: 1 }}>
        {value}
      </div>
      {!isMobile && sub && (
        <div style={{ fontSize: "11px", color: colors.slate, marginTop: "4px" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export function ManagementHome({ onNavigate }) {
  const isMobile = useIsMobile();
  const [allSites, setAllSites] = useState([]);
  const [mappedSites, setMappedSites] = useState([]);
  const [geocodingCount, setGeocodingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [inspections, setInspections] = useState([]);
  const [serviceRequests, setServiceRequests] = useState([]);
  const [techRoster, setTechRoster] = useState([]);
  const [fieldReports, setFieldReports] = useState([]);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsOpen, setCardsOpen] = useState(true);
  const [topOpen, setTopOpen] = useState(true);
  const geocodeQueueRef = useRef([]);
  const geocodingRef = useRef(false);

  const _d = new Date();
  const today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, "0")}-${String(_d.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/sites/`),
      axios.get(`${API}/inspections/`),
      axios.get(`${API}/service-requests/`),
      axios.get(`${API}/tech-roster/`),
      axios.get(`${API}/field-reports/`),
      axios.get(`${API}/bills/`),
    ])
      .then(([sitesRes, inspRes, srRes, rosterRes, reportsRes, billsRes]) => {
        const active = sitesRes.data.filter(s => s.status === "active");
        setAllSites(active);
        const withCoords = active.filter(s => s.latitude && s.longitude);
        const without = active.filter(s => !s.latitude || !s.longitude);
        setMappedSites(withCoords);
        geocodeQueueRef.current = without;
        setGeocodingCount(without.length);
        if (without.length > 0) processQueue();

        setInspections(inspRes.data);
        setServiceRequests(srRes.data);
        setTechRoster(rosterRes.data);
        setFieldReports(reportsRes.data);
        setBills(billsRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh tech positions every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/tech-roster/`).then(res => setTechRoster(res.data)).catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const processQueue = () => {
    if (geocodingRef.current) return;
    geocodingRef.current = true;
    geocodeNext();
  };

  const geocodeNext = () => {
    const queue = geocodeQueueRef.current;
    if (queue.length === 0) {
      geocodingRef.current = false;
      setGeocodingCount(0);
      return;
    }
    const site = queue.shift();
    geocodeQueueRef.current = queue;

    const query = encodeURIComponent(site.site_address + ", NSW, Australia");
    fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`, {
      headers: { "Accept-Language": "en", "Accept": "application/json" },
    })
      .then(r => r.json())
      .then(data => {
        if (data && data.length > 0) {
          const lat = parseFloat(parseFloat(data[0].lat).toFixed(6));
          const lng = parseFloat(parseFloat(data[0].lon).toFixed(6));
          axios.patch(`${API}/sites/${site.id}/`, { latitude: lat, longitude: lng }).catch(() => {});
          setMappedSites(prev => [...prev, { ...site, latitude: lat, longitude: lng }]);
        } else {
          setFailedCount(prev => prev + 1);
        }
      })
      .catch(() => setFailedCount(prev => prev + 1))
      .finally(() => {
        setGeocodingCount(geocodeQueueRef.current.length);
        setTimeout(geocodeNext, NOMINATIM_DELAY_MS);
      });
  };

  // KPI calculations
  const activeSiteCount = allSites.length;
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const _thisMonth = _today.getMonth(); const _thisYear = _today.getFullYear();
  const afssOverdue = allSites.filter(s => { if (!s.afss_due_date) return false; const d = new Date(s.afss_due_date); d.setHours(0,0,0,0); return d < _today; }).length;
  const afssDue = allSites.filter(s => { if (!s.afss_due_date) return false; const d = new Date(s.afss_due_date); return d.getMonth() === _thisMonth && d.getFullYear() === _thisYear; }).length;
  const inspectionsDue = inspections.filter(i => i.status === "in_progress").length;
  const openRequests = serviceRequests.filter(sr => sr.status === "incomplete").length;
  const activeTechs = techRoster.filter(r => r.date === today && r.clocked_on && r.latitude && r.longitude);
  const techsOnDuty = techRoster.filter(r => r.date === today && r.clocked_on).length;
  const systemsOffline = fieldReports.filter(r => r.report_type === "system_offline" && !r.is_resolved).length;
  const fipFaults = fieldReports.filter(r => r.report_type === "fip_fault" && !r.is_resolved).length;

  const BASIC_CARRY_FROM_YM = 2026 * 12 + 5; // June 2026
  const viewYM = _thisYear * 12 + _thisMonth;
  const basicDue = bills
    .filter(b => {
      if (!b.due_date || b.status === "paid" || b.status === "covered" || b.category !== "basic") return false;
      const d = new Date(b.due_date);
      const billYM = d.getFullYear() * 12 + d.getMonth();
      return billYM >= BASIC_CARRY_FROM_YM && billYM <= viewYM;
    })
    .reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
  const carriedOutstanding = (cat) => bills
    .filter(b => {
      if (!b.due_date || b.status === "paid" || b.status === "covered" || b.category !== cat) return false;
      const d = new Date(b.due_date);
      return d.getFullYear() * 12 + d.getMonth() <= viewYM;
    })
    .reduce((sum, b) => sum + parseFloat(b.amount || 0), 0);
  const supplierDue = carriedOutstanding("supplier");
  const subconDue   = carriedOutstanding("subcontractor");
  const extraDue    = carriedOutstanding("extra");
  const fmtDollar = (n) => n === 0 ? "$0" : `$${Math.round(n).toLocaleString()}`;

  const monthlyCount = allSites.filter(s => s.has_monthly_service || s.has_monthly_efsm).length;
  const monthlyValue = allSites
    .filter(s => s.has_monthly_service || s.has_monthly_efsm)
    .reduce((sum, s) => sum + parseFloat(s.monthly_fee || 0), 0);
  // allSites is already active-only; drop sites whose customer is archived too.
  const contractSites = allSites.filter(s => s.account_status === "active");
  const contractsValue = contractSites
    .reduce((sum, s) => sum + (parseFloat(s.annual_value) || 0), 0);

  const accentFor = (count) => count > 0 ? colors.red : colors.green;

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: colors.slate, fontSize: "14px" }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#f8fafc", position: "relative" }}>
      {/* KPI strip — 7 cards */}
      {topOpen && <div style={{
        backgroundColor: colors.white,
        borderBottom: `1px solid ${colors.border}`,
        padding: isMobile ? "12px 14px" : "14px 20px",
        flexShrink: 0,
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(7, 1fr)",
        gap: isMobile ? "8px" : "10px",
      }}>
        <KpiCard
          label="Active Sites"
          value={activeSiteCount}
          sub={`${mappedSites.length} mapped${geocodingCount > 0 ? ` · locating ${geocodingCount}…` : ""}`}
          accentColor={colors.teal}
          onClick={onNavigate ? () => onNavigate("sites") : null}
        />
        <KpiCard
          label="Techs On Duty"
          value={techsOnDuty}
          sub={activeTechs.length > 0 ? `${activeTechs.length} on map` : "Clocked on today"}
          accentColor={techsOnDuty > 0 ? colors.amber : "#94a3b8"}
        />
        <KpiCard
          label="AFSS Due"
          value={afssDue}
          sub={afssOverdue > 0 ? `${afssOverdue} overdue` : "This month"}
          accentColor={afssOverdue > 0 ? colors.red : accentFor(afssDue)}
          onClick={onNavigate ? () => onNavigate("afss") : null}
        />
        <KpiCard
          label="Inspections Due"
          value={inspectionsDue}
          sub="In progress"
          accentColor={accentFor(inspectionsDue)}
          onClick={onNavigate ? () => onNavigate("inspections") : null}
        />
        <KpiCard
          label="Customer Requests"
          value={openRequests}
          sub="Open"
          accentColor={openRequests > 0 ? colors.amber : colors.green}
          onClick={onNavigate ? () => onNavigate("requests") : null}
        />
        <KpiCard
          label="Systems Offline"
          value={systemsOffline}
          sub="Unresolved reports"
          accentColor={systemsOffline > 0 ? colors.red : colors.green}
          onClick={onNavigate ? () => onNavigate("field-reports") : null}
        />
        <KpiCard
          label="FIP Faults"
          value={fipFaults}
          sub="Unresolved reports"
          accentColor={fipFaults > 0 ? "#ea580c" : colors.green}
          onClick={onNavigate ? () => onNavigate("field-reports") : null}
        />
      </div>}

      {/* Map */}
      <div style={{ flex: 1, minHeight: 0, position: "relative", isolation: "isolate" }}>
        <MapContainer
          center={[-32.5, 147.5]}
          zoom={6}
          style={{ height: "100%", width: "100%" }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {mappedSites.length > 0 && <FitBounds sites={mappedSites} />}

          {/* Site pins */}
          {mappedSites.map(site => (
            <Marker
              key={`site-${site.id}`}
              position={[parseFloat(site.latitude), parseFloat(site.longitude)]}
              icon={pinIcon}
            >
              <Popup maxWidth={240}>
                <div style={{ fontFamily: "Inter, sans-serif", padding: "4px 2px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: colors.darkGrey, marginBottom: "4px" }}>
                    {site.site_name}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.slate, marginBottom: "6px", lineHeight: "1.4" }}>
                    {site.site_address}
                  </div>
                  {site.account_name && (
                    <div style={{ fontSize: "11px", fontWeight: "600", color: colors.teal, borderTop: `1px solid ${colors.border}`, paddingTop: "6px" }}>
                      {site.account_name}
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          ))}

          {/* Tech pins — amber circles with initials */}
          {activeTechs.map(r => (
            <Marker
              key={`tech-${r.id}`}
              position={[parseFloat(r.latitude), parseFloat(r.longitude)]}
              icon={makeTechIcon(r.tech_name)}
            >
              <Popup maxWidth={200}>
                <div style={{ fontFamily: "Inter, sans-serif", padding: "4px 2px" }}>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: colors.darkGrey, marginBottom: "4px" }}>
                    {r.tech_name}
                  </div>
                  <div style={{ fontSize: "12px", color: colors.amber, fontWeight: "600" }}>
                    On duty today
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Floating toggle button — top-left, controls top KPI strip */}
        <button
          onClick={() => setTopOpen(o => !o)}
          title={topOpen ? "Hide site stats" : "Show site stats"}
          style={{
            position: "absolute", top: "42%", left: "10px",
            transform: "translateY(-50%)",
            zIndex: 1000,
            backgroundColor: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "20px",
            padding: "6px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: "pointer",
            display: "flex", alignItems: "center", gap: "5px",
            fontSize: "12px", fontWeight: 700, color: colors.text,
          }}
        >
          <span style={{
            display: "inline-block",
            transform: topOpen ? "rotate(-90deg)" : "rotate(90deg)",
            transition: "transform 0.2s",
            fontSize: "13px", lineHeight: 1,
          }}>›</span>
          <span>KPI</span>
        </button>

        {/* Floating toggle button — top-right of map, always accessible */}
        <button
          onClick={() => setCardsOpen(o => !o)}
          title={cardsOpen ? "Hide financial summary" : "Show financial summary"}
          style={{
            position: "absolute", top: "42%", right: "10px",
            transform: "translateY(-50%)",
            zIndex: 1000,
            backgroundColor: colors.white,
            border: `1px solid ${colors.border}`,
            borderRadius: "20px",
            padding: "6px 12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            cursor: "pointer",
            display: "flex", alignItems: "center", gap: "5px",
            fontSize: "12px", fontWeight: 700, color: colors.text,
          }}
        >
          <span>$</span>
          <span style={{
            display: "inline-block",
            transform: cardsOpen ? "rotate(90deg)" : "rotate(-90deg)",
            transition: "transform 0.2s",
            fontSize: "13px", lineHeight: 1,
          }}>›</span>
        </button>
      </div>

      {/* Stats panel — floating overlay, shown when cardsOpen */}
      {cardsOpen && (
        <div style={{
          position: "absolute", bottom: 0, left: 0, right: 0,
          zIndex: 500,
          backgroundColor: colors.white,
          boxShadow: "0 -2px 16px rgba(0,0,0,0.10)",
          borderTop: `1px solid ${colors.border}`,
        }}>
          {/* Bills category cards */}
          <div style={{
            backgroundColor: colors.white,
            padding: isMobile ? "10px 14px" : "12px 20px",
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
            gap: isMobile ? "8px" : "10px",
          }}>
            <KpiCard
              label="Basic Bills Due"
              value={fmtDollar(basicDue)}
              sub="Incl. carried forward"
              accentColor={basicDue > 0 ? colors.red : colors.green}
              onClick={onNavigate ? () => onNavigate("bills", { cat: "basic" }) : null}
            />
            <KpiCard
              label="Supplier Bills Due"
              value={fmtDollar(supplierDue)}
              sub="Incl. carried forward"
              accentColor={supplierDue > 0 ? colors.red : colors.green}
              onClick={onNavigate ? () => onNavigate("bills", { cat: "supplier" }) : null}
            />
            <KpiCard
              label="Sub-Contractor Bills Due"
              value={fmtDollar(subconDue)}
              sub="Incl. carried forward"
              accentColor={subconDue > 0 ? colors.red : colors.green}
              onClick={onNavigate ? () => onNavigate("bills", { cat: "subcontractor" }) : null}
            />
            <KpiCard
              label="Extra Bills Due"
              value={fmtDollar(extraDue)}
              sub="Incl. carried forward"
              accentColor={extraDue > 0 ? colors.red : colors.green}
              onClick={onNavigate ? () => onNavigate("bills", { cat: "extra" }) : null}
            />
          </div>

          {/* Monthly services value + contract value */}
          <div style={{
            backgroundColor: colors.white,
            borderTop: `1px solid ${colors.border}`,
            padding: isMobile ? "10px 14px" : "12px 20px",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? "8px" : "10px",
          }}>
            <KpiCard
              label="Monthly Services Value"
              value={fmtDollar(monthlyValue)}
              sub={`${monthlyCount} site${monthlyCount !== 1 ? "s" : ""} on monthly service`}
              accentColor="#16a34a"
              onClick={onNavigate ? () => onNavigate("monthly-service") : null}
            />
            <KpiCard
              label="Value of Contracts"
              value={fmtDollar(contractsValue)}
              sub={`${contractSites.length} active site${contractSites.length !== 1 ? "s" : ""}`}
              accentColor={colors.orange}
              onClick={onNavigate ? () => onNavigate("accounts") : null}
            />
          </div>
        </div>
      )}
    </div>
  );
}
